import {
    CheckBoxWithLabelWidget,
} from '@tableau/widgets';
import { Formik, FormikProps } from 'formik';
import React from 'react';

import { Messages } from '~src/codegen/Localize';
import { FormikTextBox } from '~src/commonInfra/components/FormikField';
import { openReactActionDialog, ReactActionDialogBodyProps } from '~src/commonInfra/dialogs/ReactActionDialog';
import { HelpService } from '~src/commonInfra/services/HelpService';

import { cssClass } from '../../style';
import { IConnectedApplicationInfo } from '../models/ConnectedApplicationInfo';
import { ConnectedApplications } from '../resources/ConnectedApplicationsResource';
import { CreateEasConnectedApplicationResult } from '../serverApi/requests/CreateConnectedApplicationEas';

const URL_REGEX = /(\b(https):\/\/[-A-Z0-9+&@#\/%?=~_|!:,.;]*)/;

export namespace ConnectedApplicationEasDialogTestIds {
    export const EasDescription = 'connected-application-eas-description';
    export const IssuerUrl = 'connected-application-issuer-url';
    export const JwksUri = 'connected-application-jwks-uri';
    export const EasName = 'connected-application-eas-name';
}

export type EasDialogContentProps = {
    isEdit: boolean;
};

// First is the initial props, second is the state
export type EasApplicationDialogProps = ReactActionDialogBodyProps<
    EasDialogContentProps,
    Partial<IConnectedApplicationInfo>
>;

function actionDisabled(props: EasDialogContentProps, state: Partial<IConnectedApplicationInfo>): boolean {
    // Issuer URL should have a value and jwksUri is optional
    return !state.issuerUrl || !URL_REGEX.test(state.issuerUrl);
}

function action(props: EasDialogContentProps, state: Partial<IConnectedApplicationInfo>): Promise<CreateEasConnectedApplicationResult> {
    if (state.id) {
        return ConnectedApplications.updateConnectedAppsEas({
            id: state.id,
            enabled: state.enabled,
            issuerUrl: state.issuerUrl,
            jwksUri: state.jwksUri,
            name: state.name
        });
    }
    return ConnectedApplications.createEas({
        enabled: state.enabled,
        issuerUrl: state.issuerUrl,
        jwksUri: state.jwksUri,
        name: state.name
    });
}

export async function openConnectedApplicationEasDialog(previous?: IConnectedApplicationInfo): Promise<CreateEasConnectedApplicationResult> {
    const initialDialogState = { ...previous };
    const initialDialogProps = { isEdit: Boolean(previous) };
    const modal = openReactActionDialog({
        title: previous ? Messages.connectedApplicationsEditEas() : Messages.connectedApplicationsSetupNewConnectedApp(),
        bodyComponent: EasDialogBoxContent,
        dialogWidth: 509,
        initialDialogState,
        initialBodyProps: initialDialogProps,
        actionName: previous
            ? Messages.update()
            : Messages.create(),
        action,
        actionDisabled,
        dismissDialogOnActionFailed: true
    });
    return modal.result;
}

export function EasDialogBoxContent(props: EasApplicationDialogProps): JSX.Element {
    const cssPrefix = 'CreateEasConfigDialog';
    const isValid = (issuerUrl: string) => {
        return issuerUrl &&  !URL_REGEX.test(issuerUrl) ? Messages.connectedApplicationsLinkFormat(): undefined;
    };

    type EasFormProps = {
        issuerUrl: string;
        jwksUri: string;
        name: string;
    };

    const dialogStyle = cssClass(cssPrefix, 'EasDialog', {
        maxWidth: '100%',
        overflowX: 'hidden',
    });

    const gridStyle = cssClass(cssPrefix, 'Grid', {
        display: 'grid',
        gridTemplateColumns: '190px 375px',
        gap: '10',
        gridAutoRows: `50px 50px 50px 30px`,
        paddingTop: 20,
        textAlign: 'left',
    });

    const easCreateDescription = cssClass(cssPrefix, 'EasDescription', {
        margin: 'auto',
        $nest: {
            'a:link': {
                textDecoration: 'none',
            },
            'a:hover': {
                textDecoration: 'underline',
            },
        },
    });

    const nameLabelStyle = cssClass(cssPrefix, 'NameLabel', {
        gridArea: '1 / 1 / 2 / 2',
      });

      const nameBoxStyle = cssClass(cssPrefix, 'NameBox', {
        gridArea: '1 / 2 / 2 / 3',
      });

    const issuerUrlLabelStyle = cssClass(cssPrefix, 'IssuerUrlLabel', {
        gridArea: '2 / 1 / 3 / 2',
    });

    const issuerUrlBoxStyle = cssClass(cssPrefix, 'IssuerUrlBox', {
        gridArea: '2 / 2 / 3 / 3',
    });

    const jwksUriLabelStyle = cssClass(cssPrefix, 'JwksUriLabel', {
        gridArea: '3 / 1 / 4 / 2',
    });

    const jwksUriBoxStyle = cssClass(cssPrefix, 'JwksUriBox', {
        gridArea: '3 / 2 / 4 / 3',
    });

    const activateRadioButtonStyle = cssClass(cssPrefix, 'activateRadioButton', {
        gridArea: '4 / 1 / 5 / 2',
    });

    return (
        <div className={dialogStyle}>
            <div className={easCreateDescription} data-tb-test-id={ConnectedApplicationEasDialogTestIds.EasDescription}>
                {Messages.connectedApplicationsCreateDescription() + ' '}
                <a href={HelpService.getServerHelpUrlByCategory('connected-apps-eas')}>{Messages.learnMore()}</a>
            </div>
            <div className={gridStyle}>
                <Formik
                    initialValues={{issuerUrl: props.dialogState.issuerUrl, jwksUri: props.dialogState.jwksUri, name: props.dialogState.name}}
                    onSubmit={() => {}}
                >
                    {(formik: FormikProps<EasFormProps>) => (
                        <>
                            <div className={nameLabelStyle}  data-tb-test-id={ConnectedApplicationEasDialogTestIds.EasName}>
                                {Messages.connectedApplicationsName()}
                            </div>
                            <div className={nameBoxStyle}>
                                <FormikTextBox
                                    placeholder={Messages.connectedApplicationsEasNamePlaceholder()}
                                    testId={'connected-application-eas-name'}
                                    name={'name'}
                                    label={''}
                                    style={{ width: 275 }}
                                    onChange={e => {
                                        formik.handleChange(e);
                                        props.updateDialogState({ name: e.target.value });}
                                    }
                                />
                            </div>
                            <div className={issuerUrlLabelStyle} data-tb-test-id={ConnectedApplicationEasDialogTestIds.IssuerUrl}>
                                {Messages.connectedApplicationsIssuerUrl()}
                            </div>
                            <div className={issuerUrlBoxStyle}>
                                <FormikTextBox
                                    placeholder={Messages.connectedApplicationsIssuerUrlPlaceholder()}
                                    testId={'connected-application-issuer-url'}
                                    name={'issuerUrl'}
                                    label={''}
                                    style={{ width: 275 }}
                                    onChange={e => {
                                        formik.handleChange(e);
                                        props.updateDialogState({ issuerUrl: e.target.value });}
                                    }
                                    validate={isValid}
                                    showValidationMessage={true}
                                />
                            </div>
                            <div className={jwksUriLabelStyle} data-tb-test-id={ConnectedApplicationEasDialogTestIds.JwksUri}>
                                {Messages.connectedApplicationsJwksUri()}
                            </div>
                            <div className={jwksUriBoxStyle}>
                                <FormikTextBox
                                    placeholder={Messages.connectedApplicationsJwksUriPlaceholder()}
                                    testId={'connected-application-jwks-uri'}
                                    name={'jwksUri'}
                                    label={''}
                                    style={{ width: 275 }}
                                    onChange={e => {
                                        formik.handleChange(e);
                                        props.updateDialogState({ jwksUri: e.target.value });}
                                    }
                                />
                            </div>
                        </>
                    )}
                </Formik>
                {!props.isEdit &&
                    <div className={activateRadioButtonStyle}>
                        <CheckBoxWithLabelWidget
                            value={Messages.connectedApplicationsActivateUponCreation()}
                            label={Messages.connectedApplicationsActivateUponCreation()}
                            testId={'connected-applications-activate-upon-creation-checkbox'}
                            checked={props.dialogState.enabled}
                            handleChange={event => props.updateDialogState({ enabled: !props.dialogState.enabled })}
                            removeLeftMargin={true}
                        />
                    </div>
                }
            </div>
        </div>
    );
}